/**
 * Inc5 · Comparación de calificaciones entre periodos
 * (`/api/comparaciones`).
 *
 * Compara autoevaluaciones hechas con modelos distintos apoyándose en un esquema
 * de correspondencias declarado a mano. Un componente sin correspondencia no
 * aparece en el comparativo.
 *
 * Capa de servicios: una función por operación del API. No lanza nunca —cada
 * función devuelve un valor neutro ante el fallo—, así que el llamador debe
 * comprobar el resultado, no envolver en try/catch.
 */

import type { ComparacionCalificacion } from '@/interfaces/ComparacionCalificacion'
import { http, primerError } from '@/services/http'

/** Forma con la que el API de comparacion devuelve este dato. */
export interface OpcionesComparacion {
  /** Factor (componente) cuyas características se comparan. Omitir = factores de primer nivel. */
  componente?: number
  /** Ejecución del periodo anterior. Omitir = usa la enlazada (id_ejecucion_anterior). */
  anterior?: number
}

/** Forma con la que el API de comparacion devuelve este dato. */
export interface ResultadoComparacion {
  ok: boolean
  data?: ComparacionCalificacion
  status: number
  error?: string
}

/**
 * Inc5 · Comparación de calificaciones de una ejecución contra un periodo anterior.
 * GET /api/ejecuciones/{id}/comparacion?componente=&anterior=  (permiso calificacion.ver)
 */
export async function getComparacionService(
  idEjecucion: number,
  opciones: OpcionesComparacion = {}
): Promise<ResultadoComparacion> {
  const params = new URLSearchParams()
  if (opciones.componente) params.set('componente', String(opciones.componente))
  if (opciones.anterior) params.set('anterior', String(opciones.anterior))
  const query = params.toString() ? `?${params.toString()}` : ''

  const result = await http.get<ComparacionCalificacion>(`/api/ejecuciones/${idEjecucion}/comparacion${query}`)

  if (!result.ok) {
    return { ok: false, status: result.status, error: primerError(result, 'No se pudo cargar la comparación') }
  }
  return { ok: true, status: result.status, data: result.data }
}

// ── Emparejamiento manual de componentes (correspondencias) ──────────────────

export interface ComponentePlano {
  id_componente: number
  ordinal: string | null
  nombre: string
  nivel: number
}

/** Fija el emparejamiento manual: el componente posterior (actual) ↔ anterior. */
export async function emparejarComponentesService(
  idComponentePosterior: number,
  idComponenteAnterior: number
): Promise<{ ok: boolean; error?: string }> {
  const result = await http.post('/api/correspondencias', {
    id_componente_posterior: idComponentePosterior,
    id_componente_anterior: idComponenteAnterior
  })
  return result.ok ? { ok: true } : { ok: false, error: primerError(result, 'No se pudo emparejar') }
}

/** Quita el emparejamiento manual del componente posterior (vuelve al automático). */
export async function desemparejarComponenteService(
  idComponentePosterior: number
): Promise<{ ok: boolean; error?: string }> {
  const result = await http.delete(`/api/correspondencias/posterior/${idComponentePosterior}`)
  return result.ok ? { ok: true } : { ok: false, error: primerError(result, 'No se pudo quitar el emparejamiento') }
}

/** Estructura de una versión aplanada (para elegir el componente anterior a emparejar). */
export async function getComponentesVersionService(idVersion: number): Promise<ComponentePlano[]> {
  const result = await http.get<any>(`/api/versiones/${idVersion}/estructura`)
  if (!result.ok || !result.data) return []

  const planos: ComponentePlano[] = []
  const walk = (nodo: any, nivel: number) => {
    if (!nodo) return
    planos.push({
      id_componente: nodo.id_componente,
      ordinal: nodo.ordinal ?? null,
      nombre: nodo.nombre ?? '',
      nivel
    })
    for (const h of nodo.hijos_recursivos ?? []) walk(h, nivel + 1)
  }
  for (const raiz of result.data.estructura ?? []) walk(raiz, 1)
  // Omitir la raíz (nivel 1 del modelo suele ser el contenedor); dejar factores en adelante.
  return planos
}
